import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnChanges, OnDestroy, QueryList, SimpleChanges, ViewChildren } from '@angular/core';
import { map, Observable } from 'rxjs';
import { TimelineEntryComponent } from './timeline-entry/timeline-entry.component';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { TimelineService } from '../../services/timeline.service';
import { Action, CompactTimeline, Timeline, TimelineType } from '../../interfaces/timeline';
import { MatchingPeriodService } from '../../services/matchingPeriod.service';
import { ActivatedRoute, Router } from '@angular/router';
import { MatchingPeriod } from '../../interfaces/matchingPeriod';
import { PagetoolbarComponent } from '../pagetoolbar/pagetoolbar.component';
import { TimelineUlComponent } from './timeline-ul/timeline-ul.component';
import { MatchRun } from '../../interfaces/matchRun';
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-timeline',
  imports: [MatIconModule, CommonModule, TimelineEntryComponent, PagetoolbarComponent, TimelineUlComponent],
  templateUrl: './timeline.component.html',
  styleUrl: './timeline.component.scss'
})
export class TimelineComponent implements AfterViewInit, OnDestroy {
  @ViewChildren(TimelineEntryComponent, { read: ElementRef }) private containers!: QueryList<ElementRef>;

  @Input() matchingPeriod!: string;

  timelines: Timeline[] = [];
  compactTimelines: CompactTimeline[] = [];
  // Create IntersectionObservers for first and last timeline entry
  firstItemInViewObserver = new IntersectionObserver((entries) => {
    this.firstItemVisible = entries.some((entry) => entry.isIntersecting); // Check for any intersection
    this.ref.markForCheck();
  });

  lastItemInViewObserver = new IntersectionObserver((entries) => {
    this.lastItemVisible = entries.some((entry) => entry.isIntersecting); // Check for any intersection
    this.ref.markForCheck();
  });

  firstItemVisible = true;
  lastItemVisible = true;

  //Used to get navigation for match periods in timeline.
  currentTimelineId: string | null = null;
  currentPeriod: MatchingPeriod | null = null;
  previousPeriod: MatchingPeriod | null = null;
  nextPeriod: MatchingPeriod | null = null;

  constructor(
    private readonly ref: ChangeDetectorRef,
    private timelineService: TimelineService,
    private matchingPeriodService: MatchingPeriodService,
    private cdr: ChangeDetectorRef,
    private readonly route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe((params) => {
      this.currentTimelineId = params.get('id'); // Assign the 'id' parameter to the variable
      this.assignMatchingPeriods();
    });
  }

  ngOnDestroy() {
    this.cleanupObservers();
  }

  // Reset state and disconnect current observers
  private cleanupObservers() {
    this.firstItemVisible = true;
    this.lastItemVisible = true;
    this.firstItemInViewObserver.disconnect();
    this.lastItemInViewObserver.disconnect();
  }

  ngAfterViewInit() {
    // Subscribe to changes in containers
    this.containers.changes.subscribe(() => {
      this.cleanupObservers();

      // Ensure that there are elements to observe
      if (this.containers && this.containers.first) {
        this.firstItemInViewObserver.observe(this.containers.first.nativeElement);
      } else {
        console.warn('No first container element to observe.');
      }

      if (this.containers && this.containers.last) {
        this.lastItemInViewObserver.observe(this.containers.last.nativeElement);
      } else {
        console.warn('No last container element to observe.');
      }

      // Mark for check to ensure UI updates
      this.ref.markForCheck();
    });

    // Initial observation setup
    if (this.containers.first) {
      this.firstItemInViewObserver.observe(this.containers.first.nativeElement);
    }

    if (this.containers.last) {
      this.lastItemInViewObserver.observe(this.containers.last.nativeElement);
    }

    // Trigger initial UI state
    this.ref.markForCheck();
  }

  scrollUpClass() {
    const className = this.firstItemVisible && this.lastItemVisible ? 'hidden' : this.firstItemVisible ? 'cold' : 'hot';

    return className;
  }

  scrollDownClass() {
    const className = this.firstItemVisible && this.lastItemVisible ? 'hidden' : this.lastItemVisible ? 'cold' : 'hot';

    return className;
  }

  scrollToFirstItem() {
    if (this.containers.first) {
      console.log('Scrolling to first item');
      this.containers.first.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  scrollToLastItem() {
    if (this.containers.last) {
      console.log('Scrolling to last item');
      this.containers.last.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
    }
  }

  private assignMatchingPeriods() {
    this.matchingPeriodService.getAllMatchingPeriods().subscribe((res) => {
      if (this.currentTimelineId) {
        // Find the current period
        const index = res.findIndex((period) => period.matchingPeriodName === this.currentTimelineId);

        if (index !== -1) {
          this.currentPeriod = res[index]; // Set currentPeriod

          //These are the other way around, because the database gets the cycles in DESC order, therefore the mPeriod later in the array is actually an earlier mPeriod
          this.nextPeriod = index > 0 ? res[index - 1] : null; // Set previousPeriod
          this.previousPeriod = index < res.length - 1 ? res[index + 1] : null; // Set nextPeriod
        } else {
          console.log('Current timeline ID does not match any period.');
          this.router.navigate(['/']); // Navigate to dashboard
        }
      } else {
        console.log('No timeline ID found in route.');
        this.router.navigate(['/']); // Navigate to dashboard
      }
      this.getTimeLine();
    });
  }

  navigateTo(periodName: string): void {
    if (periodName) {
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigate([`/timeline/${periodName}`]);
      });
    }
  }

  private getTimeLine() {
    // If nextPeriod exists, it means it is not the current matchingPeriod. Therefore get timeline between periods.
    if (this.currentPeriod && this.nextPeriod) {
      this.timelineService.getThisMatchingPeriodTimeline(this.currentPeriod.dateCreated, this.nextPeriod.dateCreated).subscribe((res) => {
        res.forEach((timeline: Timeline) => {
          this.timelines.push(timeline);
        });

        // Add matchRuns to the timeline
        if (this.currentPeriod!.matchRuns) {
          this.currentPeriod!.matchRuns.forEach((matchRun: MatchRun) => {
            this.timelines.push(this.convertMatchRunToTimeline(matchRun));
          });
        }

        this.compactTimelines = this.summarizeTimelines(this.timelines);
        this.cdr.detectChanges(); // Trigger change detection
      });
    } else if (this.currentPeriod) {
      this.timelineService.getThisMatchingPeriodTimeline(this.currentPeriod.dateCreated).subscribe((res) => {
        res.forEach((timeline: Timeline) => {
          this.timelines.push(timeline);
        });

        // Add matchRuns to the timeline
        if (this.currentPeriod!.matchRuns) {
          this.currentPeriod!.matchRuns.forEach((matchRun: MatchRun) => {
            this.timelines.push(this.convertMatchRunToTimeline(matchRun));
          });
        }

        console.log(this.currentPeriod);
        this.compactTimelines = this.summarizeTimelines(this.timelines);
        this.cdr.detectChanges(); // Trigger change detection
      });
    }
  }
  private convertMatchRunToTimeline(matchRun: MatchRun): Timeline {
    return {
      id: '1', // Placeholder ID, adjust as needed
      actionByUser: matchRun.userStarted || 'System', // Use the user who started the match run or default to 'System'
      dateCreated: new Date(matchRun.dateCreated * 1000),
      date: new Date(matchRun.dateCreated * 1000),
      timelineType: TimelineType.RECIPIENT,
      action: Action.MATCHRUN, // Custom action for match runs
      actionCount: matchRun.runNumber.toString(),
      centerName: this.currentPeriod!.matchingPeriodName // Use the matching period name as the center
    };
  }
  private summarizeTimelines(timelines: Timeline[]): CompactTimeline[] {
    const groupedByCenterAndMonth = timelines.reduce(
      (acc, timeline) => {
        const date = new Date(timeline.date);
        const yearMonthKey = `${timeline.centerName}-${date.getFullYear()}-${date.getMonth() + 1}`;

        // Handle crossmatch-related actions separately
        if (
          timeline.action === Action.CROSSMATCHEXCLUDED ||
          timeline.action === Action.CROSSMATCHPOSITIVE ||
          timeline.action === Action.CROSSMATCHNEGATIVE ||
          timeline.action === Action.CROSSMATCHREMOVED
        ) {
          const crossmatchKey = `${yearMonthKey}-${timeline.action}`;
          if (!acc[crossmatchKey]) {
            acc[crossmatchKey] = {
              centerName: timeline.centerName,
              dateStart: date,
              dateEnd: date,
              create: 0,
              update: 0,
              delete: 0,
              active: 0,
              inactive: 0,
              enisupload: 0,
              approvalapproved: 0,
              approvaldenied: 0,
              approvalrequest: 0,
              crossmatchexcluded: 0,
              crossmatchpositive: 0,
              crossmatchnegative: 0,
              crossmatchremoved: 0,
              timelines: [],
              user: timeline.actionByUser
            };
          }

          // @ts-ignore
          acc[crossmatchKey][timeline.action] += parseInt(timeline.actionCount, 10) || 0;
          acc[crossmatchKey].timelines.push(timeline);

          return acc;
        }
        if (timeline.action === Action.MATCHRUN) {
          console.log('Matchrun timeline:', timeline);

          const matchRunKey = `${yearMonthKey}-matchRun-${timeline.actionCount}`;
          if (!acc[matchRunKey]) {
            acc[matchRunKey] = {
              centerName: timeline.centerName,
              dateStart: date,
              dateEnd: date,
              matchruns: 0,
              altruists: 0,
              recipients: 0,
              timelines: [],
              user: timeline.actionByUser
            };
          }
          // @ts-ignore
          acc[matchRunKey].matchruns += parseInt(timeline.actionCount, 10) || 0;
          // @ts-ignore
          acc[matchRunKey].altruists = this.currentPeriod?.matchRuns.find((run) => run.runNumber === parseInt(timeline.actionCount, 10))?.altruists || 0;
          // @ts-ignore
          acc[matchRunKey].recipients = this.currentPeriod?.matchRuns.find((run) => run.runNumber === parseInt(timeline.actionCount, 10))?.recipients || 0;
          acc[matchRunKey].timelines.push(timeline);
          return acc;
        }

        // Handle ENIS sync separately (system-wide, not per-center)
        if (timeline.action === Action.ENISSYNC) {
          const enisSyncKey = `${date.getTime()}-enissync`;
          if (!acc[enisSyncKey]) {
            acc[enisSyncKey] = {
              centerName: 'ENIS Sync',
              dateStart: date,
              dateEnd: date,
              enissync: 1,
              enissyncUpdated: parseInt(timeline.actionCount, 10) || 0,
              timelines: [],
              user: timeline.actionByUser
            };
          }
          acc[enisSyncKey].timelines.push(timeline);
          return acc;
        }

        // Handle chain-related actions separately
        if (
          timeline.action === Action.CHAINLOCKED ||
          timeline.action === Action.CHAINUNLOCKED ||
          timeline.action === Action.SELECTEDFORTRANSPLANTATION ||
          timeline.action === Action.TRANSPLANTED
        ) {
          const chainKey = `${yearMonthKey}-${timeline.action}`;
          if (!acc[chainKey]) {
            acc[chainKey] = {
              centerName: timeline.centerName,
              dateStart: date,
              dateEnd: date,
              chainlocked: 0,
              chainunlocked: 0,
              selectedfortransplantation: 0,
              transplanted: 0,
              timelines: [],
              user: timeline.actionByUser
            };
          }

          // Increment the count for this specific chain action
          if (timeline.action === Action.CHAINLOCKED) {
            acc[chainKey].chainlocked = (acc[chainKey].chainlocked || 0) + 1;
          } else if (timeline.action === Action.CHAINUNLOCKED) {
            acc[chainKey].chainunlocked = (acc[chainKey].chainunlocked || 0) + 1;
          } else if (timeline.action === Action.SELECTEDFORTRANSPLANTATION) {
            acc[chainKey].selectedfortransplantation = (acc[chainKey].selectedfortransplantation || 0) + 1;
          } else if (timeline.action === Action.TRANSPLANTED) {
            acc[chainKey].transplanted = (acc[chainKey].transplanted || 0) + 1;
          }

          acc[chainKey].timelines.push(timeline);
          return acc;
        }
        // Handle other actions as before
        if (!acc[yearMonthKey]) {
          acc[yearMonthKey] = {
            centerName: timeline.centerName,
            dateStart: date,
            dateEnd: date,
            create: 0,
            update: 0,
            delete: 0,
            active: 0,
            inactive: 0,
            enisupload: 0,
            approvalapproved: 0,
            approvaldenied: 0,
            approvalrequest: 0,
            crossmatchexcluded: 0,
            crossmatchpositive: 0,
            crossmatchnegative: 0,
            crossmatchremoved: 0,
            timelines: [],
            user: timeline.actionByUser
          };
        }
        // @ts-ignore
        acc[yearMonthKey][timeline.action] += parseInt(timeline.actionCount, 10) || 0;
        acc[yearMonthKey].timelines.push(timeline);
        return acc;
      },
      {} as { [key: string]: CompactTimeline }
    );

    // Convert the grouped object into an array and sort by date
    return Object.values(groupedByCenterAndMonth).sort((a, b) => {
      const timeDiff = a.dateStart.getTime() - b.dateStart.getTime();

      // If dates are the same, prioritize ENIS sync to appear first
      if (timeDiff === 0) {
        const aIsEnisSync = a.enissync ? 1 : 0;
        const bIsEnisSync = b.enissync ? 1 : 0;
        return bIsEnisSync - aIsEnisSync; // ENIS sync (1) comes before non-ENIS sync (0)
      }

      return timeDiff;
    });
  }
}
